import React, {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import {useRightDrawerOffset} from '../../../../shared/hooks/useRightDrawerOffset';
import styled from 'styled-components';

const DRAWER_WIDTH = 640;

/**
 * 640, not the 480 the agent drawer uses: that is a chat panel and this is a
 * form. The creation form measures 673px of content, so the width is here to give
 * the field rows room rather than to buy vertical space back.
 */
const Frame = styled.div<{isOpen: boolean}>`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: ${DRAWER_WIDTH}px;
	transform: translateX(${({isOpen}) => (isOpen ? '0' : `${DRAWER_WIDTH}px`)});
	transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear 250ms;
	/* translateX moves the frame off-screen but leaves its contents FOCUSABLE, so
	   a keyboard user would tab through the whole closed form. visibility removes
	   it from the tab order; the delay lets the slide-out finish first. aria-hidden
	   is not a substitute -- it hides from assistive tech without removing focus,
	   which is itself a violation. */
	visibility: ${({isOpen}) => (isOpen ? 'visible' : 'hidden')};
	/* Legacy scoping-page chrome rather than design-system tokens. This drawer
	   hangs off a page built in the older style and has to match it; a v2-themed
	   panel beside it would read as a different product. The controls inside are
	   design-system components -- it is only the frame that is legacy. */
	background: #ffffff;
	border-left: 1px solid #e6e6e6;
	box-shadow: -3px 0 12px rgba(0, 0, 0, 0.1);
	display: flex;
	flex-direction: column;
	z-index: 100;
`;

const Header = styled.div`
	flex-shrink: 0;
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	padding: 18px 20px 14px 20px;
	border-bottom: 1px solid #e6e6e6;
`;

/**
 * 20px where the title names the thing being made, 17px where it is a section
 * heading sitting beside a chip that already carries the name. Both sizes come
 * from the design; which one applies is the caller's call, not this shell's.
 */
const Title = styled.div<{size: number}>`
	font-size: ${({size}) => size}px;
	/* 500, not 600. The heading names the surface; it is not competing with the
	   fields under it. */
	font-weight: 500;
	line-height: ${({size}) => size + 4}px;
	color: rgba(0, 0, 0, 0.87);
`;

/** Holds the title next to whatever the caller puts before it. */
const TitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: 9px;
`;

const Subtitle = styled.div`
	font-size: 12px;
	font-weight: 350;
	letter-spacing: 0.3px;
	color: #6b6d7b;
	margin-top: 3px;
`;

const CloseButton = styled.button`
	border: 0;
	background: transparent;
	padding: 4px;
	cursor: pointer;
	color: #7d7d7d;
	line-height: 0;

	&:focus-visible {
		outline: 2px solid #6e0fea;
		outline-offset: 2px;
	}
`;

/** The only scrolling region, so the footer below it cannot be scrolled away. */
const Body = styled.div`
	flex-grow: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 18px 20px;
	display: flex;
	flex-direction: column;
	gap: 14px;
`;

const Footer = styled.div`
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 10px;
	padding: 14px 20px;
	border-top: 1px solid #e6e6e6;
	background: #ffffff;
`;

export interface TaskSetDrawerProps {
	isOpen: boolean;
	title: string;
	subtitle?: string;
	onClose: () => void;
	children: React.ReactNode;
	footer?: React.ReactNode;
	/** Rendered immediately before the title, on the same line. */
	titleAdornment?: React.ReactNode;
	/** 20 by default. 17 where the title is a section heading. */
	titleSize?: number;
}

/**
 * A right-hand drawer for the scoping page.
 *
 * The footer sits outside the scrolling body on purpose, so the primary action is
 * never scrolled out of view. That matters more than it sounds: when this form
 * overflowed, the part below the fold was the preview -- the only place the user
 * learns that 26 real tasks and their revenue are about to be created now rather
 * than appearing on a schedule. Scrolling is acceptable; scrolling to reach the
 * confirmation surface is not.
 */
export const TaskSetDrawer = ({
	isOpen,
	title,
	subtitle,
	onClose,
	children,
	footer,
	titleAdornment,
	titleSize = 20,
}: TaskSetDrawerProps) => {
	const {formatMessage} = useIntl();
	// The support-chat launcher is pinned to the viewport's bottom-right and would
	// otherwise sit on top of this drawer's footer, covering the primary action.
	// It already steps aside for the agent drawer; this tells it how far to step
	// for this one.
	useRightDrawerOffset(isOpen, DRAWER_WIDTH);
	const returnFocusTo = useRef<Element | null>(null);
	// The page passes onClose as an inline arrow, so its identity changes on every
	// render. Depending on it directly would re-run the effects constantly, and the
	// focus cleanup would pull focus back to the trigger mid-typing. Read it through
	// a ref and key both effects on isOpen alone.
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	// Escape only. Cheap to bind, and separated so it cannot disturb focus.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onCloseRef.current();
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isOpen]);

	// Capture on open, restore on close. Runs exactly twice per open/close cycle.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		returnFocusTo.current = document.activeElement;
		return () => (returnFocusTo.current as HTMLElement | null)?.focus?.();
	}, [isOpen]);

	return (
		<Frame isOpen={isOpen} role="dialog" aria-label={title}>
			<Header>
				<div>
					<TitleRow>
						{titleAdornment}
						<Title size={titleSize}>{title}</Title>
					</TitleRow>
					{subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
				</div>
				<CloseButton type="button" onClick={onClose} aria-label={formatMessage({id: 'common.close'})}>
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
						<path d="M5.5 5.5l9 9M14.5 5.5l-9 9" strokeLinecap="round" />
					</svg>
				</CloseButton>
			</Header>
			<Body>{children}</Body>
			{footer ? <Footer>{footer}</Footer> : null}
		</Frame>
	);
};
